/**
 * What a reaction is on the wire, and the questions a reaction pill asks of one.
 *
 * A reaction travels as a plain string (`MessageReaction.type`): the native emoji for
 * a Unicode emoji, and a `:shortcode:` for the one custom emoji — the Birdwatch logo —
 * because an image cannot be a string. The mobile apps store the same shortcode, so
 * it is fixed: change it here and every reaction already stored stops rendering.
 */

import type {
  Channel,
  ChannelMessage,
  ChannelMessageReaction,
} from "../_api/channel-endpoints";
import { isOptimisticMessage } from "./optimistic-message";

/** The Birdwatch-logo reaction, as stored. */
export const BW_LOGO_REACTION = ":birdwatch-bw-logo:";

/**
 * Whether the viewer may add or remove reactions here right now: the channel type
 * offers them, and the channel takes new messages (`acceptsMessages` — not frozen, and
 * not archived unless it is the team's notes thread) — the server answers 409 to a
 * reaction in a closed channel, so offering one would only produce an error.
 */
export function canReactInChannel(
  channel: Pick<Channel, "features" | "acceptsMessages">,
): boolean {
  return channel.features.reactions && channel.acceptsMessages;
}

/**
 * Whether a bubble draws its reaction cluster: the channel type offers reactions, the
 * message is really on the server, and someone has reacted.
 *
 * A closed channel still SHOWS its pills — read-only, because the server answers 409 to
 * the write. That is `canReactInChannel`'s business, not this one's.
 */
export function hasVisibleReactions(
  channel: Pick<Channel, "features">,
  message: Pick<ChannelMessage, "id" | "reactions">,
): boolean {
  return (
    channel.features.reactions &&
    !isOptimisticMessage(message) &&
    (message.reactions ?? []).length > 0
  );
}

/** The slice of an emoji-mart selection this module reads. */
type PickedEmoji = { native?: string; src?: string };

/**
 * The reaction string for an emoji-mart pick: the native character, or the Birdwatch
 * shortcode for the custom image emoji (the only one with a `src`).
 */
export function toReactionType(emoji: PickedEmoji): string | null {
  if (emoji.src) return BW_LOGO_REACTION;
  return emoji.native || null;
}

/** Whether the viewer is among the people who reacted with this emoji. */
export function hasViewerReacted(
  reaction: ChannelMessageReaction,
  viewerId: string | undefined,
): boolean {
  return (
    !!viewerId && reaction.users.some((reactor) => reactor.id === viewerId)
  );
}

/**
 * A hard ceiling on the pills one message renders.
 *
 * Not a layout rule — the row wraps and the expander shows the rest, so the row itself
 * needs no cap. This guards the pathological message: every pill is a Radix tooltip root,
 * and a thread where someone has stacked fifty distinct emoji on one message would mount
 * fifty of them per bubble for no one's benefit.
 */
export const REACTION_RENDER_LIMIT = 16;

/**
 * The message's reactions in the order the row draws them.
 *
 * Most-reacted first, except that every emoji the viewer personally reacted with is
 * hoisted to the front: taking a reaction back has to stay one click, so the viewer's own
 * pill is never the one that falls past the first line into the collapsed region.
 *
 * The sort is stable, so emoji with equal counts keep the server's first-use order and
 * pills do not swap places between renders.
 *
 * This decides which pills land on line one. It does NOT decide how many fit — the row
 * wraps and reports its own overflow, so the fit is the browser's answer at the real
 * width rather than a constant guessed here.
 */
export function rankReactions(
  reactions: ChannelMessageReaction[],
  viewerId: string | undefined,
): ChannelMessageReaction[] {
  const byCount = [...reactions].sort((a, b) => b.count - a.count);
  const mine = byCount.filter((reaction) =>
    hasViewerReacted(reaction, viewerId),
  );
  const theirs = byCount.filter(
    (reaction) => !hasViewerReacted(reaction, viewerId),
  );

  return [...mine, ...theirs].slice(0, REACTION_RENDER_LIMIT);
}

/** How many names a tooltip lists before it summarizes the rest. */
const MAX_NAMED_REACTORS = 3;

/**
 * Everyone who reacted with this emoji, named — the full list the reaction details
 * surface spells out, where {@link describeReactors} gives the pill tooltip's truncated
 * one.
 *
 * The viewer always leads as "You"; a reactor with no first name is "Someone".
 */
export function listReactorNames(
  reaction: ChannelMessageReaction,
  viewerId: string | undefined,
): string[] {
  return [
    ...(hasViewerReacted(reaction, viewerId) ? ["You"] : []),
    ...reaction.users
      .filter((reactor) => reactor.id !== viewerId)
      .map((reactor) => reactor.firstName || "Someone"),
  ];
}

/**
 * Who reacted, for the pill's tooltip: "You, Kevin and 2 others".
 */
export function describeReactors(
  reaction: ChannelMessageReaction,
  viewerId: string | undefined,
): string {
  const names = listReactorNames(reaction, viewerId);

  const named = names.slice(0, MAX_NAMED_REACTORS);
  const othersCount = names.length - named.length;
  if (othersCount > 0) {
    return `${named.join(", ")} and ${othersCount} ${othersCount === 1 ? "other" : "others"}`;
  }
  if (named.length <= 1) return named.join("");
  return `${named.slice(0, -1).join(", ")} and ${named.at(-1)}`;
}

/** The viewer, as a reaction records a reactor. */
export type ReactionViewer = ChannelMessageReaction["users"][number];

/**
 * The message's reactions with the viewer's own reaction to one emoji flipped — what the
 * pill shows the instant it is clicked, before the write answers.
 *
 * Pure, and the same shape the server would answer with: `count` is the group's own
 * `users.length` (the mapper derives it the same way), a group the viewer was alone in
 * disappears when they take it back, and a brand-new emoji is appended rather than
 * inserted, because groups ride in first-use order.
 */
export function toggleViewerReaction(
  reactions: readonly ChannelMessageReaction[],
  type: string,
  viewer: ReactionViewer,
): ChannelMessageReaction[] {
  const existing = reactions.find((reaction) => reaction.type === type);
  if (!existing) return [...reactions, { type, count: 1, users: [viewer] }];

  const users = hasViewerReacted(existing, viewer.id)
    ? existing.users.filter((reactor) => reactor.id !== viewer.id)
    : [...existing.users, viewer];

  if (users.length === 0) {
    return reactions.filter((reaction) => reaction.type !== type);
  }
  return reactions.map((reaction) =>
    reaction.type === type
      ? { ...reaction, count: users.length, users }
      : reaction,
  );
}
